import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useDebouncedValue } from '@/hooks/use-debounce';
import { useCandidates } from '@/hooks/use-candidates';
import { useBulkAssignCandidates } from '@/hooks/use-recruitment';
import { vacancyAddNotice } from '../lib/bulk-assign-notice';
import { CONSENT_LOCKED_HINT, isConsentLocked } from '../lib/consent-actions';
import { extractErrorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import type { VacancyDetail } from '@/types/recruitment';
import { Check, Search, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

interface AddExistingCandidatesDialogProps {
  vacancy: VacancyDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface SelectedCandidate {
  id: string;
  fullName: string;
}

export function AddExistingCandidatesDialog({
  vacancy,
  open,
  onOpenChange,
}: AddExistingCandidatesDialogProps) {
  const stages = useMemo(
    () => [...vacancy.pipelineStages].sort((a, b) => a.position - b.position),
    [vacancy.pipelineStages],
  );
  const firstStageId = stages[0]?.id ?? '';

  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<SelectedCandidate[]>([]);
  const [stageId, setStageId] = useState<string>(firstStageId);
  const debouncedSearch = useDebouncedValue(search, 300);

  useEffect(() => {
    if (!open) {
      setSearch('');
      setSelected([]);
      setStageId(firstStageId);
    } else {
      setStageId(firstStageId);
    }
  }, [open, firstStageId]);

  const candidatesQuery = useCandidates({
    excludeVacancyId: vacancy.id,
    search: debouncedSearch || undefined,
    status: 'ACTIVE',
    limit: 20,
  });
  const candidates = candidatesQuery.data?.data ?? [];

  const bulkMutation = useBulkAssignCandidates(vacancy.id);

  const selectedIds = useMemo(() => new Set(selected.map((c) => c.id)), [selected]);

  const toggleCandidate = (c: { id: string; fullName: string }) => {
    setSelected((prev) =>
      prev.some((p) => p.id === c.id)
        ? prev.filter((p) => p.id !== c.id)
        : [...prev, { id: c.id, fullName: c.fullName }],
    );
  };

  const removeSelected = (id: string) => {
    setSelected((prev) => prev.filter((p) => p.id !== id));
  };

  const handleSave = async () => {
    if (selected.length === 0 || !stageId) return;
    try {
      const result = await bulkMutation.mutateAsync({
        candidateIds: selected.map((c) => c.id),
        stageId,
      });
      const notice = vacancyAddNotice(result);
      toast[notice.tone](notice.text);
      onOpenChange(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Failed to add candidates. Try again.'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Add existing candidates</DialogTitle>
          <DialogDescription className="wrap-anywhere">
            Pick one or more candidates and place them at a stage of the {vacancy.title} pipeline.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {selected.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {selected.map((c) => (
                <span
                  key={c.id}
                  className="inline-flex max-w-xs items-center gap-1 rounded-full bg-hygge/15 px-2 py-0.5 text-xs text-hygge"
                >
                  <span className="truncate" title={c.fullName}>
                    {c.fullName}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeSelected(c.id)}
                    className="rounded-full p-0.5 hover:bg-hygge/25"
                    aria-label={`Remove ${c.fullName}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, or skill…"
              className="pl-9"
              autoFocus
            />
          </div>

          <div className="max-h-[280px] overflow-y-auto rounded-md border border-border">
            {candidatesQuery.isLoading ? (
              <div className="p-4 text-center text-sm text-muted-foreground">Loading…</div>
            ) : candidates.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                {debouncedSearch
                  ? 'No candidates match your search.'
                  : 'No candidates available to add.'}
              </div>
            ) : (
              <ul>
                {candidates.map((c) => {
                  const isSelected = selectedIds.has(c.id);
                  // HW-1055: a candidate who revoked consent is not added to vacancies.
                  const locked = isConsentLocked(c);
                  return (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => toggleCandidate(c)}
                        disabled={locked}
                        title={locked ? CONSENT_LOCKED_HINT : undefined}
                        className={cn(
                          'flex w-full cursor-pointer items-center gap-3 border-b border-border px-3 py-2 text-left transition-colors last:border-b-0',
                          isSelected ? 'bg-hygge/10' : 'hover:bg-muted/50',
                          locked && 'cursor-not-allowed opacity-50 hover:bg-transparent',
                        )}
                      >
                        <span
                          className={cn(
                            'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                            isSelected
                              ? 'border-hygge bg-cta text-cta-foreground'
                              : 'border-muted-foreground/40',
                          )}
                        >
                          {isSelected && <Check className="h-3 w-3" />}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium" title={c.fullName}>
                            {c.fullName}
                          </div>
                          <div
                            className="truncate text-xs text-muted-foreground"
                            title={c.email ?? c.headline ?? undefined}
                          >
                            {locked
                              ? 'Consent revoked — cannot be added'
                              : (c.email ?? c.headline ?? 'No contact details')}
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="add-existing-stage">Add to stage</Label>
            <Select value={stageId} onValueChange={setStageId}>
              <SelectTrigger id="add-existing-stage" className="w-full">
                <SelectValue placeholder="Select a stage" />
              </SelectTrigger>
              <SelectContent>
                {stages.map((s) => (
                  <SelectItem
                    key={s.id}
                    value={s.id}
                    className="[&>span:last-child]:truncate"
                    title={s.name}
                  >
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="accent"
            onClick={handleSave}
            disabled={selected.length === 0 || !stageId || bulkMutation.isPending}
          >
            {bulkMutation.isPending
              ? 'Adding…'
              : `Add ${selected.length || ''}${
                  selected.length === 0
                    ? ''
                    : selected.length === 1
                      ? ' candidate'
                      : ' candidates'
                }`.trim()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
